/**
 * NotificationsPage — full notification inbox for AquaStar end-users.
 *
 * Route: /notifications (authed)
 *
 * Shows all meeting invites in a paginated list with mark-read support.
 * This is the P1-62 scoped subset of the full P1-58 notification inbox —
 * currently only meeting_invite kinds are rendered. When P1-57 (app-layer
 * notifications) ships, other notification kinds will appear here too.
 *
 * Accessible via:
 *   - "View all" link in the NotificationsBell popover
 *   - Direct navigation to /notifications
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { deleteInvite, getInvites, markInviteRead } from '../lib/collabApi';
import type { CollabInvite } from '../lib/collabApi';

export function NotificationsPage() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState<CollabInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await getInvites({ limit: 50 });
      setInvites(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function onMarkRead(invite: CollabInvite) {
    if (invite.readAt) return;
    // Optimistic update
    setInvites((prev) => prev.map((i) => i.id === invite.id ? { ...i, readAt: new Date().toISOString() } : i));
    try {
      await markInviteRead(invite.id);
    } catch {
      // Revert on failure
      setInvites((prev) => prev.map((i) => i.id === invite.id ? { ...i, readAt: null } : i));
    }
  }

  function onJoin(invite: CollabInvite) {
    void onMarkRead(invite);
    navigate(`/collab/${invite.roomId}`, {
      state: { inviteId: invite.id, roomName: invite.roomName },
    });
  }

  async function onDelete(invite: CollabInvite) {
    // Optimistic remove
    const snapshot = invites;
    setInvites((prev) => prev.filter((i) => i.id !== invite.id));
    try {
      await deleteInvite(invite.id);
    } catch {
      // Revert
      setInvites(snapshot);
    }
  }

  function relTime(iso: string) {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60_000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  const unreadCount = invites.filter((i) => !i.readAt).length;

  return (
    <div className="min-vh-100" style={{ background: 'var(--aqua-bg)' }}>
      <AppHeader />

      <main className="container py-4" style={{ maxWidth: 600 }}>
        {/* Page header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="mb-0 fw-bold" style={{ color: 'var(--aqua-text-strong)' }}>
              Notifications
            </h4>
            {unreadCount > 0 && (
              <span className="text-muted small">{unreadCount} unread</span>
            )}
          </div>
          {unreadCount > 0 && !loading && (
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={async () => {
                const unread = invites.filter((i) => !i.readAt);
                await Promise.all(unread.map((i) => markInviteRead(i.id)));
                setInvites((prev) => prev.map((i) => ({ ...i, readAt: i.readAt ?? new Date().toISOString() })));
              }}
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="alert alert-danger">
            {error}{' '}
            <button className="btn btn-link btn-sm p-0" onClick={load}>Retry</button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="d-flex align-items-center justify-content-center py-5 text-muted">
            <span className="spinner-border spinner-border-sm me-2" /> Loading…
          </div>
        )}

        {/* Empty */}
        {!loading && !error && invites.length === 0 && (
          <div
            className="text-center py-5 text-muted"
            style={{
              background: 'var(--aqua-surface)',
              border: '1px solid var(--aqua-border)',
              borderRadius: 12,
            }}
          >
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🔔</div>
            <div className="fw-semibold mb-1">No notifications yet</div>
            <div className="small">Meeting invitations will appear here.</div>
          </div>
        )}

        {/* Invite list */}
        {!loading && invites.length > 0 && (
          <div
            style={{
              background: 'var(--aqua-surface)',
              border: '1px solid var(--aqua-border)',
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            {invites.map((invite, idx) => (
              <div
                key={invite.id}
                style={{
                  borderBottom: idx < invites.length - 1 ? '1px solid var(--aqua-border)' : undefined,
                  background: invite.readAt ? undefined : 'rgba(var(--bs-primary-rgb), 0.04)',
                  padding: '1rem 1.25rem',
                }}
              >
                <div className="d-flex align-items-start justify-content-between gap-3">
                  {/* Icon + text */}
                  <div className="d-flex align-items-start gap-3 flex-grow-1">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                      style={{
                        width: 40, height: 40,
                        background: invite.readAt ? 'var(--aqua-surface-muted)' : 'rgba(var(--bs-primary-rgb), 0.12)',
                        fontSize: '1.2rem',
                      }}
                    >
                      🎥
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {!invite.readAt && (
                          <span
                            className="rounded-circle flex-shrink-0"
                            style={{ width: 7, height: 7, background: 'var(--aqua-primary)', display: 'inline-block' }}
                          />
                        )}
                        <span className="fw-semibold" style={{ color: 'var(--aqua-text-strong)', fontSize: '0.9rem' }}>
                          {invite.sessionId
                            ? `Meeting started in ${invite.roomName}`
                            : `You were added to ${invite.roomName}`}
                        </span>
                      </div>
                      {invite.startedByName && (
                        <div className="text-muted small">
                          {invite.sessionId
                            ? `Started by ${invite.startedByName}`
                            : `Added by ${invite.startedByName}`}
                        </div>
                      )}
                      <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>
                        {relTime(invite.createdAt)}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="d-flex gap-2 flex-shrink-0">
                    {!invite.readAt && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => void onMarkRead(invite)}
                        title="Mark as read"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ fontSize: '0.75rem', borderRadius: 6 }}
                      onClick={() => onJoin(invite)}
                    >
                      {invite.sessionId ? 'Join' : 'Open'}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => void onDelete(invite)}
                      title="Delete this notification"
                      aria-label="Delete notification"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
